import { ThemeEvents } from '@theme/events';
import { GUARD_GROUP, PICKER, findBlockingPicker, guardedRadios, showSizeError } from '@theme/pdp-utils';

/**
 * Fandom variant picker (blocks/variant-picker.liquid with Design "Fandom").
 *
 * The markup stays Horizon's <variant-picker> with its radios, refs and variant JSON. The Fandom state lives in
 * attributes on the <variant-picker class="pdp-variants"> element itself, because Horizon morphs only the picker's
 * children from the page it fetches for each variant:
 * - data-pdp-guard: the first non-colour option (`.pdp-option--guard`, the "size") must be chosen before adding.
 * - data-pdp-unchosen: no size chosen yet (rendered on a visit without ?variant=). The first size choice removes it.
 * - data-pdp-size-error: "Please choose a size to continue." is showing.
 *
 * Add to cart (the main button, any outside button with form="BuyButtons-ProductForm-…" such as the sticky bar, or
 * Enter in the quantity field) is intercepted on `window` in the capture phase. That runs before Horizon's
 * document-level on:click / on:submit delegation, so no request is sent and the "Added" animation does not run.
 *
 * <pdp-size-guide> opens its size-chart dialog and closes it on ×, Escape or a backdrop click, returning focus to the
 * link that opened it.
 */

/**
 * @param {HTMLFormElement | null | undefined} form
 * @returns {HTMLElement | null}
 */
function blockingPicker(form) {
  if (!(form instanceof HTMLFormElement) || form.dataset.type !== 'add-to-cart-form') return null;
  return findBlockingPicker(form.closest('dialog, .shopify-section') ?? document);
}

/**
 * @param {Event} event
 * @param {HTMLFormElement | null | undefined} form
 */
function interceptAdd(event, form) {
  const picker = blockingPicker(form);
  if (!picker) return;
  event.preventDefault();
  event.stopImmediatePropagation();
  showSizeError(picker);
}

if (!window.__pdpVariantsBound) {
  window.__pdpVariantsBound = true;

  window.addEventListener(
    'click',
    (event) => {
      const target = event.target instanceof Element ? event.target.closest('button[type="submit"], input[type="submit"]') : null;
      if (target instanceof HTMLButtonElement || target instanceof HTMLInputElement) interceptAdd(event, target.form);
    },
    { capture: true }
  );

  window.addEventListener(
    'submit',
    (event) => {
      if (event.target instanceof HTMLFormElement) interceptAdd(event, event.target);
    },
    { capture: true }
  );

  // A choice updates its label at once (the morph that follows renders the same text) and the first size choice
  // ends the "unchosen" state.
  document.addEventListener('change', (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || input.type !== 'radio') return;
    const picker = input.closest(PICKER);
    const group = input.closest('.pdp-option');
    if (!picker || !group) return;

    const value = group.querySelector('.pdp-option__value');
    if (value) value.textContent = input.value;

    if (group.matches(GUARD_GROUP)) {
      picker.removeAttribute('data-pdp-unchosen');
      picker.removeAttribute('data-pdp-size-error');
    }
  });

  // Runs synchronously after Horizon morphed the picker from the fetched page, before the next paint. While no size is
  // chosen (a colour was changed), clear the size the fetched page selected. Once chosen, make sure the chosen size is
  // checked even if the fetched page rendered it unchecked.
  document.addEventListener(ThemeEvents.variantUpdate, (event) => {
    const picker = event.target;
    if (!(picker instanceof HTMLElement) || !picker.matches(PICKER)) return;
    const radios = guardedRadios(picker);
    if (!radios.length) return;

    if (picker.hasAttribute('data-pdp-unchosen')) {
      for (const radio of radios) radio.checked = false;
      return;
    }

    if (radios.some((radio) => radio.checked)) return;
    const position = Number(picker.querySelector(GUARD_GROUP)?.getAttribute('data-option-position') || 0);
    const value = /** @type {CustomEvent} */ (event).detail?.resource?.options?.[position - 1];
    const match = radios.find((radio) => radio.value === value);
    if (match) match.checked = true;
  });
}

/**
 * <pdp-size-guide>: the "Size guide" link and its dialog (snippets/pdp-size-guide.liquid).
 */
class PdpSizeGuide extends HTMLElement {
  /** @type {HTMLElement | null} */
  #opener = null;

  connectedCallback() {
    this.addEventListener('click', this.#onClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
  }

  /** @returns {HTMLDialogElement | null} */
  get dialog() {
    const dialog = this.querySelector('dialog');
    return dialog instanceof HTMLDialogElement ? dialog : null;
  }

  /** @param {HTMLElement | null} [opener] - Receives focus again when the dialog closes. */
  open(opener = null) {
    const { dialog } = this;
    if (!dialog || dialog.open) return;
    this.#opener = opener;
    dialog.addEventListener('close', this.#onClose, { once: true });
    dialog.showModal();
  }

  close() {
    this.dialog?.close();
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const opener = target.closest('[data-pdp-size-guide-open]');
    if (opener instanceof HTMLElement) {
      event.preventDefault();
      this.open(opener);
      return;
    }

    // The inner wrapper fills the dialog, so a click on the dialog element itself is a click on the backdrop.
    if (target.closest('[data-pdp-size-guide-close]') || target === this.dialog) this.close();
  };

  #onClose = () => {
    const opener = this.#opener;
    this.#opener = null;
    if (opener?.isConnected) opener.focus();
  };
}

if (!customElements.get('pdp-size-guide')) {
  customElements.define('pdp-size-guide', PdpSizeGuide);
}
